import React from 'react';
import { Card, CardBody, CardTitle, Input, Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Tooltip, Progress } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import Moment from 'moment';
import { dailyRepairsData, repairChartOptions, weeklyRepairChartOptions } from './total-repairs-chart';
import './total-repairs.css';

class TotalRepairs extends React.Component {
  requestSequence = 0;
  state = {
    currentDataSetting: 1, currentItem: 'Today', isOpen: false, tooltipOpen: false,
    startDate: Moment().format('YYYY-MM-DD'), endDate: Moment().format('YYYY-MM-DD'),
    currentDate: Moment().format('YYYY-MM-DD'), currentUser: JSON.parse(localStorage.getItem('currentUser')),
    repairsByDateData: { labels: [], datasets: [] }, totalRepairs: 0, loading: true, error: ''
  };

  componentDidMount() { this.getData(); }
  componentWillUnmount() { this.requestSequence += 1; }

  selectDropDownItem = (id, title) => {
    const endDate = Moment().format('YYYY-MM-DD');
    const startDate = id === 1 ? endDate : Moment().subtract(1, id === 2 ? 'week' : 'month').format('YYYY-MM-DD');
    this.setState({ currentDataSetting: id, currentItem: title, startDate, endDate }, this.getData);
  };

  step = direction => {
    const unit = this.state.currentDataSetting === 1 ? 'day' : this.state.currentDataSetting === 2 ? 'week' : 'month';
    this.setState(state => ({
      startDate: Moment(state.startDate).add(direction, unit).format('YYYY-MM-DD'),
      endDate: Moment(state.endDate).add(direction, unit).format('YYYY-MM-DD')
    }), this.getData);
  };

  handleChange = event => {
    this.requestSequence += 1;
    this.setState({ [event.target.name]: event.target.value, currentDataSetting: 4, currentItem: 'Custom Range', loading: false, error: '', rangePending: true });
  };

  getData = async () => {
    const sequence = ++this.requestSequence;
    const { startDate, endDate, currentDataSetting, currentUser } = this.state;
    if (!Moment(startDate, 'YYYY-MM-DD', true).isValid() || !Moment(endDate, 'YYYY-MM-DD', true).isValid() || startDate > endDate) {
      this.setState({ loading: false, error: 'Choose a valid start and end date.' });
      return;
    }
    this.setState({ loading: true, error: '', rangePending: false });
    const daily = currentDataSetting === 2 || currentDataSetting === 3;
    const route = daily ? 'get-total-repairs-by-day-date-range' : 'get-total-repairs-date-range';
    try {
      const response = await fetch(`https://cqzybp52y3.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}${route}?StartDate=${startDate}&EndDate=${endDate}`, { headers: { Authorization: currentUser.jwtToken } });
      const body = await response.json();
      if (sequence !== this.requestSequence) return;
      if (!response.ok || body.status !== 'Success' || !Array.isArray(body.response)) throw new Error('Repair totals could not be loaded. Please retry.');
      this.setState(daily ? { repairsByDateData: dailyRepairsData(body.response, startDate, endDate), loading: false } : { totalRepairs: Number((body.response[0] || {}).total || 0), loading: false });
    } catch (error) {
      if (sequence === this.requestSequence) this.setState({ loading: false, error: error.message });
    }
  };

  render() {
    const { currentDataSetting, currentItem, startDate, endDate, loading, error, rangePending } = this.state;
    const daily = currentDataSetting === 2 || currentDataSetting === 3;
    const navigation = direction => <Button size="sm" color="link" onClick={() => this.step(direction)} aria-label={direction < 0 ? 'Previous repair period' : 'Next repair period'} disabled={direction > 0 && endDate >= this.state.currentDate}><i className={`mdi mdi-arrow-${direction < 0 ? 'left' : 'right'} mdi-24px`} /></Button>;
    return <Card className="card-hover total-repairs-card"><CardBody>
      <div className="total-repairs-header">
        <CardTitle>Total Repairs <i className="mdi mdi-information-outline ml-1" id="TotalRepairstooltip" style={{ color: '#fff' }} />
          <Tooltip placement="top" isOpen={this.state.tooltipOpen} target="TotalRepairstooltip" toggle={() => this.setState(state => ({ tooltipOpen: !state.tooltipOpen }))}>Counts repair records by the date they were created.</Tooltip>
        </CardTitle>
        <ButtonDropdown isOpen={this.state.isOpen} toggle={() => this.setState(state => ({ isOpen: !state.isOpen }))}>
          <DropdownToggle color="dark" caret>{currentItem}</DropdownToggle>
          <DropdownMenu right>{['Today', 'Week', 'Month', 'Custom Range'].map((title, i) => <DropdownItem key={title} onClick={() => this.selectDropDownItem(i + 1, title)}>{title}</DropdownItem>)}</DropdownMenu>
        </ButtonDropdown>
      </div>
      <div className="total-repairs-range">Created: {Moment(startDate, 'YYYY-MM-DD', true).isValid() ? Moment(startDate).format('MM/DD/YYYY') : 'Start date'}{startDate !== endDate && ` – ${Moment(endDate, 'YYYY-MM-DD', true).isValid() ? Moment(endDate).format('MM/DD/YYYY') : 'End date'}`}</div>
      {loading || error || rangePending ? <div className="total-repairs-status" role={error ? 'alert' : 'status'}>
        {loading ? 'Loading repairs…' : error || 'Select GO to apply this date range.'}
        {error && <Button color="dark" size="sm" onClick={this.getData}>Retry</Button>}
      </div> : <div className="total-repairs-chart-row">
        {currentDataSetting !== 4 && navigation(-1)}
        {daily ? <div className="total-repairs-plot"><Bar data={this.state.repairsByDateData} options={currentDataSetting === 2 ? weeklyRepairChartOptions : repairChartOptions} /></div> : <div className="total-repairs-count">{this.state.totalRepairs}<Progress color="warning" value={this.state.totalRepairs} className="mt-2" /></div>}
        {currentDataSetting !== 4 && navigation(1)}
      </div>}
      <div className="total-repairs-dates">
        <label>{currentDataSetting === 1 ? 'Day' : 'From'}<Input aria-label="Total repairs start date" name="startDate" size="sm" type="date" value={startDate} onChange={this.handleChange} /></label>
        {currentDataSetting !== 1 && <label>To<Input aria-label="Total repairs end date" name="endDate" size="sm" type="date" value={endDate} onChange={this.handleChange} /></label>}
        <Button color="dark" size="sm" onClick={this.getData} disabled={currentDataSetting !== 4 || loading}>GO</Button>
      </div>
    </CardBody></Card>;
  }
}
export default TotalRepairs;
